import React from 'react';
import { Grid, IconButton, Accordion, AccordionSummary, AccordionDetails, Typography, List, ListItem, ListItemText, Container, listItemSecondaryActionClasses } from '@mui/material';
import { Edit, Delete, Visibility, Add } from '@mui/icons-material';
import { PeriodizationsServices } from '../../services/periodizations.service';
import { PeriodizationType, PeriodizationInstanceType, PeriodizationWorkoutsType } from '../../types/periodizations.type';
import { useSnackbar } from '../../hooks/snackbar.hook';
import { HttpResponse } from '../../services/base.service';
import { useNavigate } from 'react-router-dom';
import { useDialog } from '../../hooks/dialog.hook';
import { AppToolbar } from '../../components/AppToolbar';
import { AppButton } from '../../components/AppButton';

export function PeriodizationsList() {
    const [periodizations, setPeriodizations] = React.useState<PeriodizationType[]>([]);
    const { showSnackbar } = useSnackbar();
    const { showConfirmation } = useDialog();
    const navigate = useNavigate();

    React.useEffect(() => {
        fetchPeriodizations();
    }, []);

    const fetchPeriodizations = async () => {
        try {
            const resp: HttpResponse<PeriodizationType[]> = await PeriodizationsServices.getAll();
            setPeriodizations(resp.data || []);
        } catch (err) {
            showSnackbar('Error fetching periodizations', 'error');
        }
    };

    const handleAdd = () => {
        navigate('create');
    };


    const editPeriodization = (id: number) => {
        navigate(`edit/${id}`);
    };

    const deletePeriodization = (p: PeriodizationType) => {
        showConfirmation(`Are you sure you want to delete the ${p.name} periodizaton?`, () => {
            PeriodizationsServices.deleteById(p.id).then((response: HttpResponse) => {
                setPeriodizations(periodizations.filter((c) => c.id !== p.id));
            });
        });
    };

    const handleEditWorkout = (periodizationId: number) => {
        navigate(`/periodizations/exercises/edit/?instance_id=${periodizationId}`);
    };

    const previewWorkout = (instanceId: number) => {
        navigate(`/periodizations/preview?instance_id=${instanceId}`);
    };

    const renderWorkoutList = (workouts: PeriodizationWorkoutsType[]) => {
        return (workouts?.map((w: PeriodizationWorkoutsType) => (
            <Grid item xs={2.3} key={w.id}>
                <Accordion expanded sx={{
                    height: 240, // fixed height
                    '& .MuiAccordionDetails-root': {
                        padding: 1,
                        height: '100%',
                        overflowY: 'auto', // scroll if content overflows
                    },
                }}>
                    <AccordionDetails style={{ padding: 0 }}>
                        <Typography style={{ fontWeight: 'bold' }}>{w.workout?.name}</Typography>
                        <List>
                            {w.class_instances?.map((instance: PeriodizationInstanceType, index: number) => {
                                const date = new Date(instance.date);
                                const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
                                const day = date.getUTCDate();
                                const label = w.week_day != null ? `${dayOfWeek} (${day})` : `Week ${index + 1}`;
                                return (
                                    <ListItem
                                        style={{ paddingTop: 1, paddingBottom: 0 }}
                                        key={instance.id}
                                        secondaryAction={
                                            <>
                                                <IconButton edge="end" onClick={() => handleEditWorkout(instance.id)}>
                                                    <Edit />
                                                </IconButton>
                                                <IconButton edge="end" onClick={() => previewWorkout(instance.id)}>
                                                    <Visibility />
                                                </IconButton>
                                            </>

                                        }
                                    >
                                        <ListItemText primary={label} />
                                    </ListItem>
                                )
                            })}
                        </List>
                    </AccordionDetails>
                </Accordion>
            </Grid>
        )));
    }

    return (
        <Container>
            <AppToolbar title='Periodizations'>
                <AppButton
                    startIcon={<Add />}
                    onClick={handleAdd}
                >
                    Add Periodization
                </AppButton>
            </AppToolbar>
            <Grid container spacing={2}>
                {periodizations.map((p) => {
                    //const totalWeeks = getWeeksBetweenDates(p.start_date, p.end_date);
                    const fixedWorkouts = p.periodization_workouts?.filter((w) => w.week_day !== null) || [];
                    const flexibleWorkouts = p.periodization_workouts?.filter((w) => w.week_day === null) || [];
                    return (
                        <Grid item xs={12} key={p.id}>
                            <Accordion expanded>
                                <AccordionSummary>
                                    <Grid container alignItems="center" justifyContent="space-between">
                                        <Grid item>
                                            <Typography component="span" style={{ color: '#1976d2', marginRight: 8, fontWeight: 'bold' }}>
                                                {p.name}
                                            </Typography>
                                            <Typography component="span" style={{ fontSize: 14, color: '#1976d2' }}>
                                                {`( ${p.start_date} - ${p.end_date} )`}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <IconButton onClick={() => editPeriodization(p.id)}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton onClick={() => deletePeriodization(p)}>
                                                <Delete />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container gap={1}>
                                        {fixedWorkouts.length > 0 && (
                                            renderWorkoutList(fixedWorkouts)
                                        )}
                                        {flexibleWorkouts.length > 0 && (
                                            renderWorkoutList(flexibleWorkouts)
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    )
                })
                }
            </Grid >
        </Container>
    );
}